'use client';
import { TrackedEvents } from '@config/index';
import routes from '@config/routes';
import {
  CHECKOUT_START_DATETIME,
  PACKAGE_STORAGE,
  SELECTED_TICKET_STORAGE,
  SOCIAL_LOGIN_REDIRECT,
} from '@config/storageKeys';
import { useCurrencyContext } from '@contexts/currency-context';
import {
  trackGa4AddToCart,
  trackGa4PackageComponentRemoved,
} from '@v2/analytics/ga4';
import { useAppBottomHeaderRef } from '@hooks/use-app-bottom-header';
import { useBuildPackageBundle } from '@hooks/use-build-package-bundle';
import { useGetShoppingCartById } from '@hooks/use-get-shopping-cart';
import { usePackagesParams } from '@hooks/use-packages-params';
import { readAdminReplaceParams } from '@hooks/use-admin-replace-params';
import { useBetterMediaQuery } from '@hooks/use-ssr-media-query';
import useStorage from '@hooks/useStorage';
import instance from '@instance';
import { LeagueEvent } from '@interfaces/APITravel';
import type { TicketGroup } from '@interfaces/ticketevolution/tikets';
import {
  ExtendedShippedItem,
  ShipmentFormat,
  ShipmentType,
} from '@interfaces/ticketevolution/tikets';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useTicketsStore } from '@store/tickets-store';
import { skipPackageComponent } from '@utils/skip-package-component';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { CheckoutHeaderQuote } from '@v2/components/BuyPackageHeader/checkout-header-quote';
import getCookie from '@utils/get-cookie';
import { links, v2Links } from '@utils/navigation/links';
import { showToast } from '@utils/toasts';
import { updateShoppingCart } from '@v2/action/shopping-cart/shopping-cart-action';
import {
  CheckoutPayload,
  PackageDetails,
  ShoppingCartStepEnum,
  TicketDetails,
} from '@v2/action/shopping-cart/types';
import { Button } from '@v2/ui';
import { normalizeTicketNotes } from '@v2/utils';
import { setCookie } from 'cookies-next';
import moment from 'moment-timezone';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TICKET_SELECTION_FORM_ID } from './ConfirmSelection';
import { getTotalPriceLabel } from './utils/get-total-price-label';
import { getTicketQuotePrice } from '../utils/ticket-quote-price';
import type { SelectedTicketQuote } from '../utils/use-selected-ticket-quote';
import {
  getTicketTaxQuote,
  prefetchTicketTaxQuote,
  sumWholesaleTicketTax,
} from '../utils/ticket-tax-quotes';

type Props = {
  event: LeagueEvent;
  minTicketPrice: number;
  minHotelPrice: number;
  selectedFlightPrice: number;
  onEstimate?: (quote: CheckoutHeaderQuote) => void;
  selectedQuote: SelectedTicketQuote;
  retryQuote: () => void;
};
// Extract price calculation logic
function calculateTotalPrice(params: {
  isBundle: boolean;
  ticketSelected: TicketGroup | null;
  minHotelPrice: number;
  ticketsQuantity: number;
  minTicketPrice: number;
  taxQuota: { tax: number };
  selectedFlightPrice: number;
}): number {
  const {
    isBundle,
    ticketSelected,
    minHotelPrice,
    ticketsQuantity,
    minTicketPrice,
    taxQuota,
    selectedFlightPrice,
  } = params;

  // Use early returns and explicit conditions
  if (isBundle && !ticketSelected) {
    const total = minHotelPrice + minTicketPrice + selectedFlightPrice;

    return total;
  }

  if (!ticketSelected && !isBundle) {
    return minTicketPrice;
  }

  const retailPrice = ticketSelected!.retail_price;

  if (!isBundle) {
    return retailPrice * ticketsQuantity + taxQuota.tax;
  }

  // For bundle: if we don't have tax data yet, use minTicketPrice approach
  // Otherwise use the specific ticket price + tax
  if (!taxQuota?.tax || taxQuota.tax <= 0) {
    // Calculate tax as the difference between minTicketPrice and selected ticket retail price
    // This maintains price consistency between selected and unselected states
    const selectedTicketSubtotal = retailPrice * ticketsQuantity;
    const taxEstimate = Math.max(0, minTicketPrice - selectedTicketSubtotal);
    const total =
      minHotelPrice +
      selectedTicketSubtotal +
      taxEstimate +
      selectedFlightPrice;

    return total;
  }

  const total =
    minHotelPrice +
    retailPrice * ticketsQuantity +
    taxQuota.tax +
    selectedFlightPrice;

  return total;
}

function determineShipmentType(
  type: ShipmentFormat,
  event: {
    venue: { time_zone: string };
    occurs_at: string;
    occurs_at_local?: string;
  }
): ShipmentType {
  const {
    venue: { time_zone },
    occurs_at,
    occurs_at_local,
  } = event;

  // Real hours until the event. `occurs_at_local` carries the venue's true UTC offset, so it is an
  // exact instant. `occurs_at` is the venue WALL clock stamped with "Z" (not a real UTC instant), so
  // it must be read as wall time in the venue zone — parsing it as UTC shifted the gap by the
  // venue's offset, which flips the FedEx / local pickup 18h cutoff.
  const eventDate = occurs_at_local
    ? moment(occurs_at_local)
    : moment.tz(
        String(occurs_at).substring(0, 19),
        time_zone || 'America/New_York'
      );
  const hoursUntilEvent = eventDate.diff(moment(), 'hours', true);

  switch (type) {
    case 'Flash_seats':
      return 'FlashSeats';
    case 'TM_mobile':
      return 'TMMobile';
    case 'Physical':
      return hoursUntilEvent > 18 ? 'FedEx' : 'LocalPickup';
    default:
      return type;
  }
}

function getTicketPriceData(rate: number, taxes: number, ticketPrice: number) {
  return ticketPrice * rate + taxes;
}
function trackTicketSelection(
  sessionData: Session | null,
  packageData: any,
  ticketDetails: TicketGroup
) {
  const userIp = getCookie('user-ip')?.toString() || 'IP not found';
  const anonymousId =
    getCookie('anonymous-user-id')?.toString() || 'anonymous-user-id not found';
  const user = sessionData?.user;
  const ticketsQuantity = useTicketsStore.getState().ticketsQuantity;
  ticketDetails;

  return instance.server(routes.track, {
    body: JSON.stringify({
      userId: user?.id ? Number(user.id) : undefined,
      anonymousId: user ? undefined : anonymousId,
      trackedEvent: TrackedEvents.TICKET_SELECTED,
      properties: JSON.stringify({
        ip: userIp,
        anonymousId,
        eventName: packageData.eventName!,
        eventDate: packageData.eventDate!,
        ticketCount: ticketsQuantity,
        ticketSection: ticketDetails.section,
        ticketRow: ticketDetails.row,
        retailPrice: ticketDetails.retail_price,
      }),
    }),
    method: 'POST',
  });
}

export function TicketSelectionBottomHeader(props: Props) {
  const {
    event,
    minHotelPrice,
    minTicketPrice,
    selectedFlightPrice,
    onEstimate,
    selectedQuote,
    retryQuote,
  } = props;
  const bottomHeaderRef = useAppBottomHeaderRef<HTMLDivElement>();
  const storage = useStorage();
  const [isLoading, setIsLoading] = useState(false);
  const isConfirmingRef = useRef(false);

  const router = useRouter();
  const { data: sessionData } = useSession();

  const ticketsQuantity = useTicketsStore((state) => state.ticketsQuantity);
  const ticketSelected = useTicketsStore((state) => state.ticketSelected);
  const isTicketSelectionConfirmed = useTicketsStore(
    (state) => state.isTicketSelectionConfirmed
  );
  const taxQuota = useTicketsStore((state) => state.taxQuota);

  const { eventId, packageType, eventLink } = usePackagesParams();
  const needsQuote = packageType === 'tickets';

  const cartId = useCheckoutStepsStore((state) => state.cartId);

  const isMobile = useBetterMediaQuery('(max-width: 1024px)');
  const { packageGuests, packageData } = useBuildPackageBundle();
  const isBundle = useBuildPackagesStore((state) => state.isBundle);
  const { priceRate, currency, priceRateLoading } = useCurrencyContext();
  const quotePending =
    needsQuote && (selectedQuote.status !== 'ready' || priceRateLoading);

  const { data: shoppingCartData } = useGetShoppingCartById();
  const adminReplace = readAdminReplaceParams(storage);

  const isFlightIncluded = packageData?.selected?.flights;

  const isHotelIncluded =
    packageType === 'packages' || packageData?.selected?.hotel;

  const totalPrice = useMemo(
    () =>
      calculateTotalPrice({
        isBundle,
        ticketSelected,
        minHotelPrice,
        ticketsQuantity,
        minTicketPrice,
        taxQuota,
        selectedFlightPrice: isFlightIncluded ? selectedFlightPrice : 0,
      }),
    [
      isBundle,
      ticketSelected,
      minHotelPrice,
      ticketsQuantity,
      minTicketPrice,
      taxQuota?.tax,
      isFlightIncluded,
      selectedFlightPrice,
    ]
  );

  const estimate = useMemo(() => {
    if (needsQuote && ticketSelected) {
      if (selectedQuote.status === 'ready' && !priceRateLoading) {
        return getTicketQuotePrice(
          ticketSelected.retail_price,
          ticketsQuantity,
          selectedQuote.quote,
          priceRate,
          currency
        );
      }
      return {
        amount: null,
        travelers: ticketsQuantity,
        currency,
        display: { perPerson: 'Calculating price…', total: '' },
      };
    }
    const perPerson = formatCurrencyV2(
      totalPrice / ticketsQuantity,
      priceRate,
      currency,
      true,
      false
    );
    const total = formatCurrencyV2(
      totalPrice,
      priceRate,
      currency,
      true,
      false
    );

    return {
      amount: totalPrice,
      travelers: ticketsQuantity,
      currency,
      display: { perPerson, total },
    };
  }, [
    totalPrice,
    ticketsQuantity,
    currency,
    priceRate,
    needsQuote,
    ticketSelected,
    selectedQuote,
    priceRateLoading,
  ]);

  useEffect(() => {
    onEstimate?.(estimate);
  }, [estimate, onEstimate]);

  const handleRedirectHotelSelection = useCallback(() => {
    if (packageType === 'packages') {
      router.push(
        `${v2Links.searchHotel}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`
      );
    }
  }, [packageType, eventId, eventLink]);

  const onTicketClick = useCallback(
    async ({ tax, tax_signature }: { tax: number; tax_signature: string }) => {
      if (!ticketSelected) return;

      const {
        signature,
        id: ticketGroupId,
        retail_price: retailPrice,
        wholesale_price: wholesalePrice,
        section,
        row,
        format: type,
        public_notes: publicNotes,
        view_type: viewType,
        tevo_section_name: tevoSectionName,
        eticket,
      } = ticketSelected;

      const result: ExtendedShippedItem = {
        price: wholesalePrice,
        wholesalePrice,
        retailPrice,
        tax,
        tax_signature: tax_signature || '',
        ticket_group_id: ticketGroupId,
        quantity: ticketsQuantity,
        seat_order: 'consecutive',
        ticket_group_signature: signature,
        stadiumName: event.venue.name || '',
        section,
        row,
        publicNotes: normalizeTicketNotes(publicNotes),
        viewType,
        venueId: event.venue?.id,
        configurationId: event.configuration?.id,
        tevoSectionName: tevoSectionName || section,
        format: type,
        eticket,
      };

      const selectedTicketPayload = {
        ...result,
        type: determineShipmentType(type as ShipmentFormat, event),
      };

      storage.setItem(
        SELECTED_TICKET_STORAGE,
        JSON.stringify(selectedTicketPayload)
      );

      const totalTicketPrice = retailPrice * ticketsQuantity + tax;
      useBuildPackagesStore.setState({
        selectedTicketPrice: totalTicketPrice,
      });

      // Admin item-replacement: the confirm page reads SELECTED_TICKET_STORAGE (just
      // written above) directly — no cart persistence needed for this throwaway
      // ADMIN_EDIT snapshot, and no hotel/checkout chaining.
      if (adminReplace.isAdminReplace) {
        router.push(v2Links.adminReplaceConfirm);
        return;
      }

      setCookie(CHECKOUT_START_DATETIME, new Date().getTime());

      const checkoutUrl = `${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`;
      storage.setItem(SOCIAL_LOGIN_REDIRECT, checkoutUrl);
      const updatedPackageData = {
        ...packageData,
        selected: { ...packageData?.selected, tickets: true },
      };
      storage.setItem(PACKAGE_STORAGE, JSON.stringify(updatedPackageData));

      useCheckoutStepsStore.setState({ ticketSelected: true });

      if (cartId) {
        const cartUpdatePayload = {
          selectedTicketDetails: selectedTicketPayload as TicketDetails,
          packageConfigDetails: {
            ...shoppingCartData?.packageConfigDetails,
            ...updatedPackageData,
            totalPrice: totalTicketPrice,
            isBundle: !!shoppingCartData?.packageConfigDetails?.isBundle,
          } as PackageDetails,
          currentStep: ShoppingCartStepEnum.TICKET_SELECTION,
        };

        // Only include flightDetails if it exists and is valid
        if (shoppingCartData?.flightDetails && isFlightIncluded) {
          await updateShoppingCart(cartId, {
            ...cartUpdatePayload,
            flightDetails: shoppingCartData.flightDetails,
          });
        } else {
          await updateShoppingCart(
            cartId,
            cartUpdatePayload as CheckoutPayload
          );
        }
      }

      if (packageType === 'packages') return handleRedirectHotelSelection();
      router.push(checkoutUrl);
    },
    [
      ticketSelected,
      ticketsQuantity,
      event,
      eventId,
      eventLink,
      packageType,
      packageData,
      cartId,
      shoppingCartData,
      isFlightIncluded,
      storage,
      router,
      adminReplace.isAdminReplace,
      handleRedirectHotelSelection,
    ]
  );

  useEffect(() => {
    if (!ticketSelected?.id) return;
    prefetchTicketTaxQuote(ticketSelected.id, ticketsQuantity);
  }, [ticketSelected?.id, ticketsQuantity]);

  const handleConfirmSelection = useCallback(async () => {
    if (isConfirmingRef.current) return;
    if (needsQuote && priceRateLoading) return;
    if (
      needsQuote &&
      (selectedQuote.status !== 'ready' ||
        selectedQuote.expiresAt <= Date.now())
    ) {
      retryQuote();
      return;
    }
    isConfirmingRef.current = true;
    setIsLoading(true);
    useTicketsStore.setState({ isTicketSelectionConfirmed: true });

    try {
      if (!ticketSelected) {
        isConfirmingRef.current = false;
        useTicketsStore.setState({ isTicketSelectionConfirmed: false });
        setIsLoading(false);
        return;
      }

      const selectedTicket = ticketSelected;
      const { id: ticketGroupId } = selectedTicket;

      // Admin item-replacement doesn't need customer-facing tracking — this
      // selection isn't a real cart-add, it's a replacement for a failed booking.
      const [taxQuota] = await Promise.all([
        needsQuote && selectedQuote.status === 'ready'
          ? Promise.resolve(selectedQuote.quote)
          : getTicketTaxQuote(ticketGroupId, ticketsQuantity),
        ...(adminReplace.isAdminReplace
          ? []
          : [trackTicketSelection(sessionData, packageData, selectedTicket)]),
      ]);

      const currentSelection = useTicketsStore.getState();
      if (
        currentSelection.ticketSelected !== selectedTicket ||
        currentSelection.ticketsQuantity !== ticketsQuantity ||
        (needsQuote &&
          selectedQuote.status === 'ready' &&
          selectedQuote.expiresAt <= Date.now())
      ) {
        isConfirmingRef.current = false;
        useTicketsStore.setState({ isTicketSelectionConfirmed: false });
        setIsLoading(false);
        if (needsQuote) retryQuote();
        return;
      }

      const tax = sumWholesaleTicketTax(taxQuota);

      if (!adminReplace.isAdminReplace) {
        trackGa4AddToCart({
          currency,
          value: selectedTicket.retail_price * ticketsQuantity + tax,
          item: {
            item_id: `ticket:${ticketGroupId}`,
            item_name: `${event.name} tickets`,
            item_category: 'tickets',
            item_category2:
              packageType === 'packages' ? 'package' : 'tickets_only',
            price: selectedTicket.retail_price,
            quantity: ticketsQuantity,
          },
          params: {
            event_id: eventId,
            package_type:
              packageType === 'packages' ? 'package' : 'tickets_only',
          },
        });
      }

      // Update tickets store
      useTicketsStore.setState({
        taxQuota: {
          tax,
          tax_signature: taxQuota.tax_signature ?? null,
        },
      });

      await onTicketClick({ tax, tax_signature: taxQuota.tax_signature || '' });
    } catch (error) {
      showToast({
        message: 'Error confirming ticket selection',
        variant: 'error',
      });
      isConfirmingRef.current = false;
      useTicketsStore.setState({ isTicketSelectionConfirmed: false });
      setIsLoading(false);
    }
  }, [
    currency,
    event,
    eventId,
    packageData,
    packageType,
    sessionData,
    ticketSelected,
    ticketsQuantity,
    needsQuote,
    selectedQuote,
    retryQuote,
    onTicketClick,
    adminReplace.isAdminReplace,
    priceRateLoading,
  ]);

  const bottomTotalPriceLabel = getTotalPriceLabel({
    isFlightIncluded,
    isHotelIncluded,
    isTicketIncluded: true,
  });

  return (
    <div
      ref={bottomHeaderRef}
      data-app-bottom-header="true"
      className="app-bottom-header fixed bottom-0 left-0 right-0 z-20 w-full bg-gray-900 pb-[max(12px,env(safe-area-inset-bottom))]"
    >
      <form
        id={TICKET_SELECTION_FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          void handleConfirmSelection();
        }}
        className="flex flex-col gap-y-3 px-4 py-3 lg:mx-auto lg:max-w-[1400px] lg:flex-row lg:items-center lg:justify-between lg:px-20 lg:py-5"
      >
        <div className="flex-1">
          <div className="flex flex-col gap-y-1">
            <h5 className="text-base font-bold text-white lg:text-lg">
              {needsQuote && ticketSelected && quotePending
                ? selectedQuote.status === 'error'
                  ? 'Unable to load ticket price'
                  : 'Calculating price…'
                : `${needsQuote && ticketSelected ? '' : 'Estimated '}${estimate.display.perPerson} per person`}
            </h5>
            <span className="text-sm font-medium text-gray-400 lg:text-sm">
              {needsQuote && ticketSelected && quotePending
                ? 'Your total will include taxes and fees.'
                : `${estimate.display.total} in total. ${needsQuote && !ticketSelected ? 'Before taxes and fees' : bottomTotalPriceLabel}`}
            </span>
          </div>
        </div>

        <div className="flex gap-x-2">
          {needsQuote && selectedQuote.status === 'error' && (
            <Button type="button" onClick={retryQuote}>
              Retry price
            </Button>
          )}
          {packageType === 'packages' && !isTicketSelectionConfirmed ? (
            <Button
              type="button"
              variant="ghost"
              size={isMobile ? 'sm' : 'md'}
              className="flex-1 border border-gray-700 bg-transparent text-white lg:w-auto lg:flex-none"
              onClick={() => {
                handleRedirectHotelSelection();
                useCheckoutStepsStore.setState({ ticketSelected: false });
                useBuildPackagesStore.setState({ selectedTicketPrice: 0 });
                if (skipPackageComponent(storage, 'tickets')) {
                  trackGa4PackageComponentRemoved({
                    component: 'tickets',
                    params: {
                      event_id: eventId,
                      package_type: 'package',
                    },
                  });
                }
              }}
            >
              {"I don't need tickets"}
            </Button>
          ) : (
            <Button
              type="submit"
              size={isMobile ? 'sm' : 'lg'}
              isLoading={isLoading}
              disabled={
                (packageType === 'tickets' && !isTicketSelectionConfirmed) ||
                quotePending
              }
              className="flex-1 lg:w-auto lg:flex-none"
              onMouseEnter={() => {
                if (!ticketSelected?.id) return;
                prefetchTicketTaxQuote(ticketSelected.id, ticketsQuantity);
              }}
            >
              Continue
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
